import { clear, del, get, set } from "idb-keyval";

const DB_PREFIX = "preswald_";
const DB_LAST_FETCH = `${DB_PREFIX}last_fetch_timestamp`;
const DB_PROJECTS = `${DB_PREFIX}projects`;
const DB_PENDING_PROMPT = `${DB_PREFIX}pending_prompt`;
const DB_HELLO_PY_CONTENT = `${DB_PREFIX}hello_py_content`;
const PROJECTS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

export interface LastFetchTimestamps {
    [key: string]: number; // organizationId -> timestamp
}

export interface ProjectCache {
    projects: any[];
    timestamp: number;
    orgId: string | null;
}

export class DBStorageService {
    /**
     * Initialize the database
     * This method doesn't do much yet but provides a hook for future initialization
     */
    async init(): Promise<void> {
        try {
            // For now, just check if we can access the IndexedDB by trying to get the last fetch timestamp
            await this.getLastFetch();
            console.log("IndexedDB initialized successfully");
        } catch (error: unknown) {
            console.error("Error initializing IndexedDB:", error);
            throw error;
        }
    }

    async getLastFetch(): Promise<LastFetchTimestamps> {
        try {
            const lastFetch = await get(DB_LAST_FETCH);
            return lastFetch || {};
        } catch (error) {
            console.error("Error getting last fetch timestamp from IndexedDB:", error);
            return {};
        }
    }

    async updateLastFetch(orgId: string, timestamp: number): Promise<void> {
        try {
            const lastFetch = await this.getLastFetch();
            lastFetch[orgId] = timestamp;
            await set(DB_LAST_FETCH, lastFetch);
        } catch (error) {
            console.error("Error updating last fetch timestamp in IndexedDB:", error);
        }
    }

    async getProjects(orgId: string | null = null): Promise<any[]> {
        try {
            const projectKey = orgId ? `${DB_PROJECTS}_${orgId}` : DB_PROJECTS;
            const projectsData = await get(projectKey);
            return projectsData?.projects || [];
        } catch (error) {
            console.error("Error getting projects from IndexedDB:", error);
            return [];
        }
    }

    async storeProjects(projects: any[], orgId: string | null = null): Promise<void> {
        try {
            const projectKey = orgId ? `${DB_PROJECTS}_${orgId}` : DB_PROJECTS;
            const timestamp = Date.now();
            await set(projectKey, { projects, timestamp, orgId });
            console.log(`Stored ${projects.length} projects in IndexedDB for ${orgId || 'user'}`);
        } catch (error) {
            console.error("Error storing projects in IndexedDB:", error);
        }
    }

    async getProjectsTimestamp(orgId: string | null = null): Promise<number | null> {
        try {
            const projectKey = orgId ? `${DB_PROJECTS}_${orgId}` : DB_PROJECTS;
            const projectsData = await get(projectKey);
            return projectsData?.timestamp || null;
        } catch (error) {
            console.error("Error getting projects timestamp from IndexedDB:", error);
            return null;
        }
    }

    async shouldRefreshProjects(orgId: string | null = null): Promise<boolean> {
        const timestamp = await this.getProjectsTimestamp(orgId);
        if (!timestamp) return true;

        const now = Date.now();
        const age = now - timestamp;
        return age > PROJECTS_CACHE_TTL;
    }

    async updateProjectStatus(projectId: string, updates: { isPublished?: boolean, status?: string, deploymentUrl?: string }, orgId: string | null = null): Promise<void> {
        try {
            const projects = await this.getProjects(orgId);
            const updatedProjects = projects.map(project =>
                project.id === projectId
                    ? { ...project, ...updates, updatedAt: Date.now() }
                    : project
            );

            await this.storeProjects(updatedProjects, orgId);
        } catch (error) {
            throw error;
        }
    }

    async clearAll(): Promise<void> {
        try {
            await clear();
            console.log("Cleared all IndexedDB storage");
        } catch (error) {
            console.error("Error clearing IndexedDB storage:", error);
        }
    }

    async clearOrgData(orgId: string): Promise<void> {
        try {
            const projectKey = `${DB_PROJECTS}_${orgId}`;

            await del(projectKey);

            const lastFetch = await this.getLastFetch();
            if (lastFetch[orgId]) {
                delete lastFetch[orgId];
                await set(DB_LAST_FETCH, lastFetch);
            }
        } catch (error) {
            console.error(`Error clearing data for org ${orgId} from IndexedDB:`, error);
        }
    }

    async storePendingPrompt(prompt: string): Promise<void> {
        try {
            await set(DB_PENDING_PROMPT, {
                prompt,
                timestamp: Date.now()
            });
            console.log("Stored pending prompt in IndexedDB");
        } catch (error) {
            console.error("Error storing pending prompt in IndexedDB:", error);
        }
    }

    async getPendingPrompt(): Promise<string | null> {
        try {
            const data = await get(DB_PENDING_PROMPT);
            return data?.prompt || null;
        } catch (error) {
            console.error("Error getting pending prompt from IndexedDB:", error);
            return null;
        }
    }

    async clearPendingPrompt(): Promise<void> {
        try {
            await del(DB_PENDING_PROMPT);
            console.log("Cleared pending prompt from IndexedDB");
        } catch (error) {
            console.error("Error clearing pending prompt from IndexedDB:", error);
        }
    }

    async hasPendingPrompt(): Promise<boolean> {
        try {
            const data = await get(DB_PENDING_PROMPT);
            return !!data?.prompt;
        } catch (error) {
            console.error("Error checking for pending prompt in IndexedDB:", error);
            return false;
        }
    }

    async storeHelloPyContent(projectId: string, content: string): Promise<void> {
        try {
            const key = `${DB_HELLO_PY_CONTENT}_${projectId}`;
            await set(key, {
                content,
                timestamp: Date.now(),
                projectId
            });
        } catch (error) {
            console.error("Error storing hello.py content in IndexedDB:", error);
        }
    }

    async getHelloPyContent(projectId: string): Promise<string | null> {
        try {
            const key = `${DB_HELLO_PY_CONTENT}_${projectId}`;
            const data = await get(key);
            return data?.content || null;
        } catch (error) {
            console.error("Error getting hello.py content from IndexedDB:", error);
            return null;
        }
    }

    async clearHelloPyContent(projectId: string): Promise<void> {
        try {
            const key = `${DB_HELLO_PY_CONTENT}_${projectId}`;
            await del(key);
        } catch (error) {
            console.error("Error clearing hello.py content from IndexedDB:", error);
        }
    }
}

export const dbStorage = new DBStorageService(); 